import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Stack, Chip, Divider, Dialog, DialogTitle, DialogContent, IconButton, Button, Checkbox, FormControlLabel, LinearProgress, Alert, CircularProgress, DialogActions, Snackbar, Card } from '@mui/material';
import { EventAvailable, History, Upcoming, Whatshot, WbSunny, AcUnit, Close as CloseIcon, Phone, Inventory2, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import TemplateSelectionDialog from '../../Billings/Templates/TemplateSelectionDialog'; // Assuming this path
import { useMain } from '../../hooks/useMain';

const UpcomingInquiry = ({ data }) => {
  const { activeBranchId, getBranch, sendBulkWhatsApp, setShowMessage, getWhatsAppTemplates } = useMain();
  const [open, setOpen] = useState(false);
  const [dayDetails, setDayDetails] = useState({ items: [], label: '', color: '', bgColor: '' });
  const [selectedClients, setSelectedClients] = useState([]); // New state for selected clients
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false); // New state for template dialog
  const [templates, setTemplates] = useState([]); // New state for fetched templates
  const [loadingTemplates, setLoadingTemplates] = useState(false); // Local state for loader
  const [ownerId, setOwnerId] = useState("");
  const [isSendingBulk, setIsSendingBulk] = useState(false); // New state for bulk send loading
  const [sendBulkProgress, setSendBulkProgress] = useState(0); // New state for bulk send progress
  const [sendBulkError, setSendBulkError] = useState(null); // New state for bulk send error
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const fetchOwnerId = async () => {
      if (activeBranchId) {
        try {
          const res = await getBranch(activeBranchId);
          const branchData = res?.data;
          const derivedId = branchData?.ownerId?.[0]?._id || branchData?.ownerId?._id || branchData?.ownerId;
          if (derivedId && typeof derivedId === 'string') {
            setOwnerId(derivedId);
          }
        } catch (error) {
          console.error("Error fetching owner ID:", error);
        }
      }
    };
    fetchOwnerId();
  }, [activeBranchId, getBranch]);

  if (!data) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Helper to get YYYY-MM-DD string in local time
  const getLocalDayStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getDaysArray = () => {
    const days = [];
    for (let i = -2; i <= 2; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      days.push(getLocalDayStr(d));
    }
    return days;
  };

  const relevantDates = getDaysArray();
  const todayStr = getLocalDayStr(today);

  const groupedData = relevantDates.reduce((acc, dateStr) => {
    acc[dateStr] = data.filter(item => {
      if (!item.dateOfFollow) return false;
      const itemDate = getLocalDayStr(new Date(item.dateOfFollow));
      return itemDate === dateStr;
    });
    return acc;
  }, {});

  const getHeaderInfo = (dateStr) => {
    const date = new Date(dateStr);
    
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayStr = getLocalDayStr(yesterday);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = getLocalDayStr(tomorrow);

    if (dateStr === todayStr) return { label: 'Today', color: '#00c853', bgColor: '#f1fbf3', icon: <EventAvailable /> };
    if (dateStr === yesterdayStr) return { label: 'Yesterday', color: '#ff9800', bgColor: '#fff9f0', icon: <History /> };
    if (dateStr === tomorrowStr) return { label: 'Tomorrow', color: '#2196f3', bgColor: '#f0f7ff', icon: <Upcoming /> };
    
    const isPast = dateStr < todayStr;
    return { 
      label: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), 
      color: isPast ? '#f44336' : '#9c27b0', 
      bgColor: isPast ? '#fff5f5' : '#fcf5ff',
      icon: isPast ? <History /> : <Upcoming /> 
    };
  };

  const getEnquiryTypeIcon = (type) => {
    if (type === 'Hot') return <Whatshot sx={{ color: '#f44336', fontSize: '1rem' }} />;
    if (type === 'Warm') return <WbSunny sx={{ color: '#ff9800', fontSize: '1rem' }} />;
    if (type === 'Cold') return <AcUnit sx={{ color: '#2196f3', fontSize: '1rem' }} />;
    return null;
  };

  const handleOpen = (items, info) => {
    if (items.length === 0) return;
    setDayDetails({ items, label: info.label, color: info.color, bgColor: info.bgColor });
    setSelectedClients([]); // Clear selection when opening new day details
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedClients([]);
    setSendBulkError(null); // Clear bulk send error on dialog close
  };

  const handleClientSelect = (clientId) => {
    setSelectedClients((prevSelected) => {
      if (prevSelected.includes(clientId)) {
        return prevSelected.filter((id) => id !== clientId);
      } else {
        return [...prevSelected, clientId];
      }
    });
  };

  const handleTemplateDialogClose = () => {
    setTemplateDialogOpen(false);
    setTemplates([]);
    setIsSendingBulk(false);
    setSendBulkProgress(0);
    setSendBulkError(null);
  };

  const handleTemplateConfirm = (templateWithVariables) => {
    setSelectedTemplateData(templateWithVariables);
    setTemplateDialogOpen(false);
    setOpenConfirmationDialog(true);
  };

  const handleConfirmationDialogClose = () => {
    if (!isSendingBulk) {
      setOpenConfirmationDialog(false);
      setSelectedTemplateData(null);
    }
  };

  const handleSendBulkWhatsApp = async () => {
    if (!sendBulkWhatsApp) {
      setSendBulkError("WhatsApp bulk message API not provided.");
      return;
    }

    if (!ownerId) {
      setSendBulkError("Owner ID is missing. Please refresh or select a branch.");
      return;
    }

    setIsSendingBulk(true);
    setSendBulkProgress(0);
    setSendBulkError(null);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setSendBulkProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 300);

      const clientDetails = dayDetails.items.filter(item => selectedClients.includes(item._id));

      const res = await sendBulkWhatsApp({
        numbers: clientDetails.map(client => client.clientNumber.toString().replace(/\D/g, '').slice(-10)),
        template_name: selectedTemplateData.templateName || selectedTemplateData.name || selectedTemplateData.template_name,
        var1: selectedTemplateData.variableValues?.var_1 || "",
        var2: selectedTemplateData.variableValues?.var_2 || "",
        var3: selectedTemplateData.variableValues?.var_3 || "",
        var4: selectedTemplateData.variableValues?.var_4 || "",
        var5: selectedTemplateData.variableValues?.var_5 || "",
        button1: selectedTemplateData.variableValues?.var_6 || "",
        button2: selectedTemplateData.variableValues?.var_7 || "",
        ownerId: ownerId,
        variables: Object.values(selectedTemplateData.variableValues || {}),
        headerFile: selectedTemplateData.headerFile || null,
      }, activeBranchId);

      clearInterval(progressInterval);
      setSendBulkProgress(100);

      if (res.statusCode === 200) {
        setSnackbar({ open: true, message: `WhatsApp messages sent successfully to ${selectedClients.length} clients`, severity: 'success' });

        setTimeout(() => {
          setOpenConfirmationDialog(false);
          handleClose(); // Close the detailed view popup
          setSelectedTemplateData(null);
        }, 2000);
      } else {
        throw new Error(res.message || 'Failed to send WhatsApp messages');
      }
    } catch (error) {
      console.error("Error sending bulk WhatsApp:", error);
      setSendBulkError(error.message || "Failed to send bulk WhatsApp messages.");
      setIsSendingBulk(false);
    }
  };
const fetchWhatsAppTemplates = async () => {
    try {
      if (!ownerId) {
        setShowMessage({
          message: 'Owner ID is missing. Please select a branch.',
          messageType: 'error'
        });
        return;
      }

      setLoadingTemplates(true);
      const res = await getWhatsAppTemplates(ownerId);
      
      const templatesData = res?.templates?.data || res?.data;

      if (templatesData && Array.isArray(templatesData)) {
        // Filter only approved templates - nested structure ke according

        const approvedTemplates = templatesData.filter(template => {
          // Check if template has languages array and first language has APPROVED status
          return template.languages &&
            template.languages.length > 0 &&
            template.languages[0].status.toUpperCase() === "APPROVED";
        });

        console.log('Approved Templates:', approvedTemplates);
        setTemplates(approvedTemplates);
        setTemplateDialogOpen(true);
      } else {
        setShowMessage({
          message: res.message || 'Failed to fetch templates',
          messageType: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setShowMessage({
        message: 'Failed to fetch templates',
        messageType: 'error'
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  return (
    <Box sx={{ mb: 4, mt: 1 }}>
      <Grid container spacing={2}>
        {relevantDates.map((dateStr) => {
          const info = getHeaderInfo(dateStr);
          const items = groupedData[dateStr];
          const isToday = dateStr === todayStr;

          return (
            <Grid item xs={12} sm={6} md={2.4} key={dateStr}>
              <Paper
                elevation={isToday ? 12 : 4}
                sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  border: '2px solid ' + info.color + '40',
                  borderTop: '12px solid ' + info.color,
                  bgcolor: info.bgColor,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: '0.3s',
                  cursor: items.length > 0 ? 'pointer' : 'default',
                  position: 'relative',
                  '&:hover': { boxShadow: items.length > 0 ? `0 12px 30px ${info.color}30` : 'none', transform: items.length > 0 ? 'translateY(-6px)' : 'none' }
                }}
                onClick={() => handleOpen(items, info)}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box sx={{ color: info.color, display: 'flex' }}>{info.icon}</Box>
                  <Typography variant="subtitle2" fontWeight="900" sx={{ color: info.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {info.label}
                  </Typography>
                  <Chip
                    label={items.length}
                    size="small"
                    sx={{
                      ml: 'auto !important',
                      height: 22,
                      fontSize: '10px',
                      fontWeight: '900',
                      bgcolor: items.length > 0 ? info.color : '#eee',
                      color: '#fff'
                    }}
                  />
                </Stack>

                <Divider sx={{ mb: 2, opacity: 0.6 }} />

                <Stack spacing={1} sx={{ flexGrow: 1 }}>
                  {items.length > 0 ? (
                    items.slice(0, 2).map((item) => (
                      <Box
                        key={item._id}
                        sx={{
                          p: 1.2,
                          borderRadius: '8px',
                          bgcolor: '#fff',
                          border: '1px solid #f0f0f0',
                          borderLeft: '6px solid ' + info.color,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                          position: 'relative'
                        }}
                      >
                        <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                          {getEnquiryTypeIcon(item.enquiryType)}
                        </Box>
                        <Typography variant="body2" fontWeight="800" sx={{ fontSize: '0.8rem', color: '#2c3e50', pr: 2 }} noWrap title={item.clientName}>
                          {item.clientName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', display: 'block' }} noWrap>
                          For: {item.enquiryFor}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1, opacity: 0.5, py: 2 }}>
                      <Typography variant="caption" fontWeight="600">No Schedule</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Detailed View Popup */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ 
          p: 3,
          borderBottom: '1px solid #e2e8f0',
          background: `linear-gradient(to right, ${dayDetails.bgColor}, #fff)`
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  color: dayDetails.color
                }}>
                  {dayDetails.label === 'Today' ? <EventAvailable /> : dayDetails.label === 'Tomorrow' ? <Upcoming /> : <History />}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="900" sx={{ color: '#1e293b', letterSpacing: -0.5 }}>
                    {dayDetails.label} <span style={{ color: dayDetails.color, fontSize: '1rem' }}>Schedule</span>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: '700', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dayDetails.color }} />
                    {dayDetails.items.length} enquiries scheduled
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <IconButton onClick={handleClose} sx={{ 
              color: '#64748b',
              bgcolor: '#fff', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
              '&:hover': { bgcolor: '#f8fafc', color: '#1e293b' } 
            }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: '#f8fafc' }}>
          {sendBulkError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSendBulkError(null)}>
              {sendBulkError}
            </Alert>
          )}

          <Box sx={{ 
            mb: 2, 
            px: 2, 
            py: 1, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            bgcolor: '#fff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedClients.length === dayDetails.items.length && dayDetails.items.length > 0}
                  indeterminate={selectedClients.length > 0 && selectedClients.length < dayDetails.items.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedClients(dayDetails.items.map(i => i._id));
                    } else {
                      setSelectedClients([]);
                    }
                  }}
                  size="small"
                />
              }
              label={<Typography variant="body2" fontWeight="700" color="text.primary">Select All</Typography>}
              sx={{ ml: 0 }}
            />
            {selectedClients.length > 0 && (
              <Chip 
                label={`${selectedClients.length} Selected`} 
                size="small" 
                color="primary" 
                sx={{ fontWeight: '800', height: 24 }} 
              />
            )}
          </Box>

          <Stack spacing={1.5}>
            {dayDetails.items?.map((item) => (
              <Box
                key={item._id}
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  bgcolor: '#fff',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': { 
                    borderColor: dayDetails.color,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={selectedClients.includes(item._id)}
                        onChange={() => handleClientSelect(item._id)}
                        size="small"
                        sx={{ 
                          color: '#cbd5e1',
                          '&.Mui-checked': { color: dayDetails.color }
                        }}
                      />
                      <Box>
                        <Typography variant="body1" fontWeight="800" sx={{ color: '#1e293b', lineHeight: 1.1 }}>
                          {item.clientName}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
                          <Phone sx={{ fontSize: '0.85rem', opacity: 0.7, color: dayDetails.color }} />
                          <Typography variant="caption" fontWeight="700">
                            {item.clientNumber}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>

                    <Chip 
                      icon={getEnquiryTypeIcon(item.enquiryType)}
                      label={item.enquiryType}
                      size="small"
                      sx={{ 
                        fontWeight: '800',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        borderRadius: '6px',
                        bgcolor: item.enquiryType === 'Hot' ? '#fff1f2' : item.enquiryType === 'Warm' ? '#fffbeb' : '#f0f9ff',
                        color: item.enquiryType === 'Hot' ? '#e11d48' : item.enquiryType === 'Warm' ? '#d97706' : '#0284c7',
                        border: '1px solid',
                        borderColor: 'currentColor',
                        '& .MuiChip-icon': { color: 'inherit', ml: 0.5 }
                      }}
                    />
                  </Stack>

                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '8px', 
                    bgcolor: '#f8fafc',
                    border: '1px solid #f1f5f9'
                  }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: '800', textTransform: 'uppercase', fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Inventory2 sx={{ fontSize: '0.8rem', color: '#64748b' }} /> Service Interest
                    </Typography>
                    <Typography variant="body2" fontWeight="700" sx={{ color: '#334155' }}>
                      {item.enquiryFor}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip 
                      label={item.leadStatus} 
                      size="small" 
                      sx={{ 
                        height: 22, 
                        fontSize: '0.7rem', 
                        fontWeight: '800',
                        bgcolor: dayDetails.color,
                        color: '#fff',
                        borderRadius: '6px'
                      }} 
                    />
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: '700' }}>
                      via {item.sourceOfEnquiry}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
           <Button
                variant="contained"
                startIcon={loadingTemplates ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
                onClick={fetchWhatsAppTemplates}
                disabled={loadingTemplates || selectedClients.length === 0 || !ownerId}
                sx={{
                  background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                  borderRadius: '10px',
                  py: 1.2,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: '800',
                  boxShadow: '0 4px 14px 0 rgba(37, 211, 102, 0.39)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #128c7e 0%, #25d366 100%)',
                    boxShadow: '0 6px 20px rgba(37, 211, 102, 0.23)',
                  },
                  '&.Mui-disabled': {
                    background: '#f1f5f9',
                    color: '#94a3b8'
                  }
                }}
              >
                {loadingTemplates ? 'Loading Templates...' : `Send to ${selectedClients.length} Selected`}
              </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <TemplateSelectionDialog
        open={templateDialogOpen}
        templates={templates}
        onClose={handleTemplateDialogClose}
        onConfirm={handleTemplateConfirm}
        isSending={isSendingBulk}
        sendProgress={sendBulkProgress}
        sendError={sendBulkError}
        setSendError={setSendBulkError}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmationDialog}
        onClose={handleConfirmationDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <WhatsAppIcon color="success" />
            <Typography variant="h6">
              {isSendingBulk ? `Sending... ${sendBulkProgress}%` : 'Confirm WhatsApp Send'}
            </Typography>
          </Box>
          {isSendingBulk && (
            <LinearProgress
              variant="determinate"
              value={sendBulkProgress}
              sx={{ mt: 1 }}
            />
          )}
        </DialogTitle>

        <DialogContent>
          {sendBulkError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendBulkError}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            You're about to send WhatsApp messages to {selectedClients.length} clients
          </Alert>

          {selectedTemplateData && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Template: <strong>{selectedTemplateData.name}</strong>
              </Typography>

              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Message Preview:
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {selectedTemplateData.bodyText || 'No message content'}
                  </Typography>
                </Card>
              </Box>

              {selectedTemplateData.variables?.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Variables to be sent:
                  </Typography>
                  {selectedTemplateData.variables.map((variable, index) => (
                    <Chip
                      key={variable}
                      label={`${variable}: ${selectedTemplateData.variableValues?.[`var_${index + 1}`] || 'Not set'}`}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                      variant="outlined"
                      color={selectedTemplateData.variableValues?.[`var_${index + 1}`] ? "success" : "default"}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleConfirmationDialogClose}
            variant="outlined"
            sx={{ borderRadius: '6px' }}
            disabled={isSendingBulk}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendBulkWhatsApp}
            variant="contained"
            startIcon={<WhatsAppIcon />}
            disabled={isSendingBulk}
            sx={{
              background: isSendingBulk
                ? '#ccc'
                : 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
              borderRadius: '6px',
              px: 3,
              '&:hover': isSendingBulk ? {} : {
                background: 'linear-gradient(135deg, #128c7e 0%, #25d366 100%)'
              },
              '&:disabled': {
                background: '#ccc'
              }
            }}
          >
            {isSendingBulk ? `Sending... ${sendBulkProgress}%` : `Send to ${selectedClients.length} Clients`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpcomingInquiry;
